import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowRight,
  Building2,
  CalendarClock,
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  FileText,
  Filter,
  GraduationCap,
  Laptop,
  MapPin,
  Search,
  Send,
} from "lucide-react";
import { elakayAutoText } from "../dataElakay";
import JobApplicationForm from "./JobApplicationForm";
import "./JobSections.css";
import { toFileUrl } from "../../../utils/fileUrl";


const EMPLOYMENT_LABELS = {
  full_time: "Full time",
  part_time: "Part time",
  contract: "Contract",
  temporary: "Temporary",
  internship: "Internship",
  volunteer: "Volunteer",
  other: "Autre",
};

const WORKPLACE_LABELS = {
  remote: "Remote",
  hybrid: "Hybrid",
  onsite: "On site",
};

const SENIORITY_LABELS = {
  entry: "Entry",
  junior: "Junior",
  mid: "Mid",
  senior: "Senior",
  lead: "Lead",
  executive: "Executive",
  any: "Tous niveaux",
};

const PERIOD_LABELS = {
  hourly: "heure",
  weekly: "semaine",
  monthly: "mois",
  annual: "an",
  project: "projet",
  custom: "custom",
};

// Exported so JobApplicationForm.jsx (shared create/edit apply form) uses
// the exact same lists instead of redefining them.
export const APPLICATION_DOCUMENT_TYPES = [
  ["cv", "CV / Resume"],
  ["cover_letter", "Lettre de motivation"],
  ["diploma", "Diplome"],
  ["certificate", "Certificat"],
  ["portfolio", "Portfolio"],
  ["other", "Autre"],
];

export const MAX_APPLICANT_SOCIAL_LINKS = 7;

export const ACCEPTED_DOCUMENT_EXTENSIONS = [".pdf", ".doc", ".docx"];
export const ACCEPTED_DOCUMENT_ACCEPT = ACCEPTED_DOCUMENT_EXTENSIONS.join(",");

export const getFileExtension = (filename = "") => {
  const dotIndex = filename.lastIndexOf(".");
  return dotIndex >= 0 ? filename.slice(dotIndex).toLowerCase() : "";
};

const formatCurrency = (value = 0, currency = "USD") => {
  const amount = Number(value || 0);
  if (!amount) return "";
  try {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency,
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${amount.toLocaleString()} ${currency}`;
  }
};

const formatDate = (value) => {
  if (!value) return "Ouvert";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Ouvert";
  return date.toLocaleDateString("fr-FR", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

// Uploaded files were stored as /documents/<f> (never served in production):
// toFileUrl maps them to /elakay_api/files/documents/<f>.
const getJobImage = (job = {}) => {
  if (job.thumbnail) return toFileUrl(job.thumbnail);
  const first = job.gallery?.[0];
  if (typeof first === "string") return toFileUrl(first);
  return toFileUrl(first?.url || "");
};

const getNodeText = (node) => {
  if (!node) return "";
  if (typeof node === "string") return node;
  if (node.text) return node.text;
  if (Array.isArray(node.content)) return node.content.map(getNodeText).join(" ");
  return "";
};

const renderRichDocument = (doc, fallback) => {
  if (typeof doc === "string" && doc.trim()) return <p>{doc}</p>;
  const content = Array.isArray(doc?.content) ? doc.content : [];
  if (!content.length) return <p>{fallback}</p>;

  return content.slice(0, 12).map((node, index) => {
    const text = getNodeText(node).trim();
    if (!text) return null;
    if (node.type === "heading") return <h3 key={index}>{text}</h3>;
    if (node.type === "bulletList" || node.type === "orderedList") {
      const items = (node.content || []).map((item, itemIndex) => {
        const itemText = getNodeText(item).trim();
        return itemText ? <li key={itemIndex}>{itemText}</li> : null;
      });
      return <ul key={index}>{items}</ul>;
    }
    return <p key={index}>{text}</p>;
  });
};

const normalizeCatalog = (data) => {
  if (Array.isArray(data)) return { jobs: data, count: data.length, facets: {} };
  return {
    jobs: data?.jobs || [],
    count: data?.count || data?.jobs?.length || 0,
    facets: data?.facets || {},
    skip: data?.skip || 0,
    limit: data?.limit || 12,
  };
};

const filterUrl = (location, updates = {}) => {
  const params = new URLSearchParams(location.search);
  Object.entries(updates).forEach(([key, value]) => {
    if (value === "" || value === null || value === undefined) params.delete(key);
    else params.set(key, value);
  });
  params.delete("skip");
  const query = params.toString();
  return `/jobs${query ? `?${query}` : ""}`;
};

const paginationUrl = (location, skip) => {
  const params = new URLSearchParams(location.search);
  if (skip > 0) params.set("skip", skip);
  else params.delete("skip");
  const query = params.toString();
  return `/jobs${query ? `?${query}` : ""}`;
};

const getSalaryLabel = (job = {}) => {
  const min = formatCurrency(job.salaryMin, job.currency);
  const max = formatCurrency(job.salaryMax, job.currency);
  if (!min && !max) return "A discuter";
  if (min && max && min !== max) return `${min} - ${max} / ${PERIOD_LABELS[job.salaryPeriod] || job.salaryPeriod}`;
  return `${min || max} / ${PERIOD_LABELS[job.salaryPeriod] || job.salaryPeriod}`;
};

const getLocationLabel = (job = {}) =>
  job.location || [job.city, job.state, job.country].filter(Boolean).join(", ") || "Flexible";

export const JobHomeSection = ({ data }) => {
  const catalog = normalizeCatalog(data);
  const jobs = catalog.jobs;
  const location = useLocation();
  const navigate = useNavigate();
  const query = new URLSearchParams(location.search);
  const activeEmployment = query.get("employmentType") || "";
  const activeWorkplace = query.get("workplaceType") || "";
  const [searchText, setSearchText] = useState(query.get("q") || "");

  useEffect(() => {
    setSearchText(query.get("q") || "");
  }, [location.search]);

  const totals = catalog.facets?.totals || {};
  const employmentCounts = useMemo(() => {
    const rows = catalog.facets?.byEmploymentType || [];
    const map = {};
    rows.forEach((row) => {
      if (row?._id) map[row._id] = row.count || 0;
    });
    return map;
  }, [catalog.facets]);

  const submitSearch = (event) => {
    event.preventDefault();
    navigate(filterUrl(location, { q: searchText.trim() }));
  };

  return (
    <div className="elakay-job-page job-home-page">
      <section className="job-home-hero">
        <div className="job-shell job-home-hero__inner">
          <div className="job-home-hero__intro">
            <p className="job-home-eyebrow">{elakayAutoText("h_c59a0b58d2b0f7", "Elakay Jobs")}</p>
            <h1>{elakayAutoText("h_1a7f1031746e9a", "Construire la prochaine equipe Elakay")}</h1>
            <p>{elakayAutoText("h_14129b3d1548d7", "Decouvrez les postes ouverts pour produit, operations, marketplace, design, support client et croissance communautaire.")}</p>
          </div>
          <form className="job-home-search" onSubmit={submitSearch}>
            <Search size={18} />
            <input
              value={searchText}
              onChange={(event) => setSearchText(event.target.value)}
              placeholder={elakayAutoText("h_a54a1164ce5f4a", "Chercher un poste, une equipe ou un lieu")}
              aria-label={elakayAutoText("h_b124409fbfef2c", "Chercher un job")}
            />
            <button type="submit" aria-label={elakayAutoText("h_dcaef5e63af73e", "Chercher")}>
              {elakayAutoText("h_dcaef5e63af73e", "Chercher")}
            </button>
          </form>
        </div>
        <div className="job-shell job-home-stats">
          <div className="job-home-stat">
            <strong>{totals.count || catalog.count}</strong>
            <span>{elakayAutoText("h_e05b9409ae38db", "Postes publies")}</span>
          </div>
          <div className="job-home-stat">
            <strong>{totals.openPositions || jobs.reduce((total, job) => total + Number(job.openings || 0), 0)}</strong>
            <span>{elakayAutoText("h_f17e266f150b68", "Places ouvertes")}</span>
          </div>
          <div className="job-home-stat">
            <strong>{new Set(jobs.map((job) => job.department).filter(Boolean)).size}</strong>
            <span>{elakayAutoText("h_594cf9dadc84d2", "Equipes actives")}</span>
          </div>
        </div>
      </section>

      <section className="job-home-filters">
        <div className="job-shell job-home-filters__row">
          <span className="job-home-filter-label">
            <Filter size={14} />
            {elakayAutoText("h_2a8e76e0d96e7c", "Filtres")}
          </span>
          <Link className={`job-home-pill ${!activeEmployment ? "active" : ""}`} to={filterUrl(location, { employmentType: "" })}>
            {elakayAutoText("h_b97ae3b4f9093f", "Tous")}
          </Link>
          {Object.keys(EMPLOYMENT_LABELS).map((type) => (
            <Link
              key={type}
              className={`job-home-pill ${activeEmployment === type ? "active" : ""}`}
              to={filterUrl(location, { employmentType: type })}
            >
              {EMPLOYMENT_LABELS[type]} {employmentCounts[type] ? `(${employmentCounts[type]})` : ""}
            </Link>
          ))}
          {Object.keys(WORKPLACE_LABELS).map((type) => (
            <Link
              key={type}
              className={`job-home-pill ${activeWorkplace === type ? "active" : ""}`}
              to={filterUrl(location, { workplaceType: activeWorkplace === type ? "" : type })}
            >
              {WORKPLACE_LABELS[type]}
            </Link>
          ))}
        </div>
      </section>

      <section className="job-shell job-home-list">
        {jobs.length === 0 && (
          <div className="job-home-empty">
            <h2>{elakayAutoText("h_215c43dcbdc65f", "Aucun job public pour ces criteres.")}</h2>
            <p>{elakayAutoText("h_317d4cf6f396a1", "Les postes publies par Elakay apparaitront ici avec les details de candidature.")}</p>
          </div>
        )}
        {jobs.map((job) => (
          <Link className="job-row" to={`/jobs/${job.slug || job._id}`} key={job._id || job.slug}>
            <div className="job-row__mark" aria-hidden="true">
              {String(job.department || job.title || "E").trim().slice(0, 1).toUpperCase()}
            </div>
            <div className="job-row__main">
              <h3>{job.title}</h3>
              <div className="job-row__meta">
                <span><Building2 size={13} />{job.department || "Elakay"}</span>
                <span><MapPin size={13} />{getLocationLabel(job)}</span>
                <span><GraduationCap size={13} />{SENIORITY_LABELS[job.seniority] || job.seniority}</span>
                <span><CalendarClock size={13} />{formatDate(job.deadline)}</span>
              </div>
            </div>
            <div className="job-row__badges">
              <span className="job-row__badge">{EMPLOYMENT_LABELS[job.employmentType] || job.employmentType}</span>
              <span className="job-row__badge">{WORKPLACE_LABELS[job.workplaceType] || job.workplaceType}</span>
            </div>
            <div className="job-row__action">
              <span>{getSalaryLabel(job)}</span>
              <ArrowRight size={18} />
            </div>
          </Link>
        ))}
        {jobs.length > 0 && (catalog.skip > 0 || catalog.skip + catalog.limit < catalog.count) && (
          <div className="job-home-pagination">
            <Link
              className={`job-home-page-btn ${catalog.skip <= 0 ? "disabled" : ""}`}
              to={catalog.skip > 0 ? paginationUrl(location, Math.max(catalog.skip - catalog.limit, 0)) : "#"}
              aria-disabled={catalog.skip <= 0}
            >
              <ChevronLeft size={16} />
              {elakayAutoText("h_b962f0ffdefee4", "Precedent")}
            </Link>
            <Link
              className={`job-home-page-btn ${catalog.skip + catalog.limit >= catalog.count ? "disabled" : ""}`}
              to={catalog.skip + catalog.limit < catalog.count ? paginationUrl(location, catalog.skip + catalog.limit) : "#"}
              aria-disabled={catalog.skip + catalog.limit >= catalog.count}
            >
              {elakayAutoText("h_596d29a7e33c76", "Next")}
              <ChevronRight size={16} />
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

export const JobDetailSection = ({ data }) => {
  const payload = data?.job ? data : { job: data };
  const job = payload?.job;

  if (!job) {
    return (
      <div className="elakay-job-page job-detail-page">
        <div className="job-shell job-detail-notfound">
          <h2>{elakayAutoText("h_8fb010930f1088", "Job introuvable.")}</h2>
          <Link className="job-detail-cta" to="/jobs">{elakayAutoText("h_fb621652e25b1e", "Retour aux jobs")}</Link>
        </div>
      </div>
    );
  }

  const image = getJobImage(job);
  const applicationCount = payload?.applicationCount || 0;

  const initials = String(job.department || job.title || "E").trim().slice(0, 1).toUpperCase();
  const defaultResponsibilities = [
    elakayAutoText("h_jobdetail_default_resp_1", "Contribuer aux objectifs de l'equipe Elakay."),
    elakayAutoText("h_jobdetail_default_resp_2", "Collaborer avec les equipes produit, operations et communaute."),
    elakayAutoText("h_jobdetail_default_resp_3", "Documenter les apprentissages et ameliorer les processus."),
  ];
  const defaultRequirements = [
    elakayAutoText("h_jobdetail_default_req_1", "Esprit autonome, fiable et oriente resultat."),
    elakayAutoText("h_jobdetail_default_req_2", "Capacite a communiquer clairement."),
    elakayAutoText("h_jobdetail_default_req_3", "Interet pour le commerce communautaire et les petites entreprises."),
  ];

  return (
    <div className="elakay-job-page job-detail-page">
      <header className="job-detail-topbar">
        <div className="job-shell job-detail-topbar__inner">
          <div className="job-detail-mark" aria-hidden="true">
            {image ? <img src={image} alt="" /> : <span>{initials}</span>}
          </div>
          <div className="job-detail-heading">
            <p className="job-home-eyebrow">{job.department || elakayAutoText("h_5741b10e729c11", "Elakay Team")}</p>
            <h1>{job.title}</h1>
            <div className="job-detail-quickfacts">
              <span><MapPin size={13} />{getLocationLabel(job)}</span>
              <span><Building2 size={13} />{EMPLOYMENT_LABELS[job.employmentType] || job.employmentType}</span>
              <span><Laptop size={13} />{WORKPLACE_LABELS[job.workplaceType] || job.workplaceType}</span>
              <span><CalendarClock size={13} />{formatDate(job.deadline)}</span>
            </div>
          </div>
          <a className="job-detail-cta" href="#job-apply">
            <Send size={16} />
            {elakayAutoText("h_jobdetail_apply_cta", "Postuler")}
          </a>
        </div>
      </header>

      <section className="job-shell job-detail-stats">
        <div className="job-detail-stat">
          <strong>{getSalaryLabel(job)}</strong>
          <span>{elakayAutoText("h_2eff6099b8b128", "Compensation")}</span>
        </div>
        <div className="job-detail-stat">
          <strong>{job.openings || 1}</strong>
          <span>{elakayAutoText("h_f2e32418126d1f", "Ouverture")}{Number(job.openings || 1) > 1 ? "s" : ""}</span>
        </div>
        <div className="job-detail-stat">
          <strong>{SENIORITY_LABELS[job.seniority] || job.seniority}</strong>
          <span>{elakayAutoText("h_058818ff083b86", "Niveau")}</span>
        </div>
        <div className="job-detail-stat">
          <strong>{applicationCount}</strong>
          <span>{elakayAutoText("h_91011d8cf99acd", "Candidatures")}</span>
        </div>
      </section>

      <section className="job-shell job-detail-grid">
        <div className="job-detail-main">
          {job.summary && <p className="job-detail-summary">{job.summary}</p>}

          <div className="job-detail-card">
            <h2>{elakayAutoText("h_23bca783e86c89", "Vue d'ensemble")}</h2>
            {renderRichDocument(job.descriptionJSON, job.description || elakayAutoText("h_jobdetail_default_description", "Description du poste a venir."))}
          </div>
          <div className="job-detail-card">
            <h2>{elakayAutoText("h_ae4f91cc350198", "Responsabilites")}</h2>
            <ul>
              {(job.responsibilities?.length ? job.responsibilities : defaultResponsibilities).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
          <div className="job-detail-card">
            <h2>{elakayAutoText("h_79427ac48121a9", "Profil recherche")}</h2>
            <ul>
              {(job.requirements?.length ? job.requirements : defaultRequirements).map((item, index) => (
                <li key={index}>{item}</li>
              ))}
            </ul>
          </div>
          {job.benefits?.length > 0 && (
            <div className="job-detail-card">
              <h2>{elakayAutoText("h_08d7f47c13f6db", "Avantages")}</h2>
              <ul>
                {job.benefits.map((item, index) => (
                  <li key={index}>{item}</li>
                ))}
              </ul>
            </div>
          )}
          {job.documents?.length > 0 && (
            <div className="job-detail-card">
              <h2>{elakayAutoText("h_687c82861c956e", "Documents")}</h2>
              <div className="job-document-list">
                {job.documents.map((document, index) => (
                  <a
                    className="job-document-link"
                    href={toFileUrl(document.url)}
                    target="_blank"
                    rel="noreferrer"
                    key={`${document.url}-${index}`}
                  >
                    <span>
                      <FileText size={17} />
                      <strong>{document.title || document.originalName || elakayAutoText("h_e214b8a2992305", "Document")}</strong>
                    </span>
                    <em>{String(document.kind || "file").toUpperCase()}</em>
                    <ExternalLink size={16} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <aside className="job-detail-apply" id="job-apply">
          <h2>{elakayAutoText("h_f280fa71caf5e6", "Candidater")}</h2>
          <JobApplicationForm job={job} mode="create" />
        </aside>
      </section>
    </div>
  );
};

export default JobHomeSection;
